// Mobile-focused biometric authentication using WebAuthn
// Supports Face ID (iOS), Touch ID (iOS/macOS), and fingerprint (Android)

interface BiometricCredential {
  id: string;
  rawId: ArrayBuffer;
  response: {
    clientDataJSON: ArrayBuffer;
    attestationObject?: ArrayBuffer;
    authenticatorData?: ArrayBuffer;
    signature?: ArrayBuffer;
  };
  type: string;
}

// Convert base64url to ArrayBuffer
function base64UrlToArrayBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// Convert ArrayBuffer to base64url
function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export class BiometricAuth {
  // Check if biometric authentication is available on this device
  static async isAvailable(): Promise<boolean> {
    try {
      if (!window.PublicKeyCredential) {
        console.log('WebAuthn not supported');
        return false;
      }

      // Check for platform authenticator (Face ID, Touch ID, fingerprint)
      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      console.log('Platform authenticator available:', available);
      
      if (!available) {
        return false;
      }

      // Additional check: try to see if we can actually use it
      // Some browsers report true but don't actually support it properly
      return available;
    } catch (error) {
      console.error('Biometric check failed:', error);
      return false;
    }
  }

  // Get user-friendly biometric type name
  static async getBiometricType(): Promise<string> {
    const ua = navigator.userAgent.toLowerCase();
    
    // Check if device has biometric capability
    if (!window.PublicKeyCredential) {
      return 'Biometric';
    }

    // iOS Detection
    if (/iphone|ipad|ipod/.test(ua)) {
      // iPhone X and newer (with notch) have Face ID
      // Check screen dimensions to detect Face ID devices
      const hasNotch = window.screen.height >= 812 && window.screen.width >= 375;
      if (hasNotch) {
        return 'Face ID';
      }
      // Older iPhones have Touch ID
      return 'Touch ID';
    } 
    
    // macOS Detection
    if (/macintosh/.test(ua)) {
      // Most modern Macs have Touch ID
      return 'Touch ID';
    } 
    
    // Android Detection
    if (/android/.test(ua)) {
      // Android devices typically use fingerprint
      return 'Fingerprint';
    }
    
    // Windows Hello or other
    if (/windows/.test(ua)) {
      return 'Windows Hello';
    }
    
    // Generic fallback
    return 'Biometric Authentication';
  }

  // Register biometric credential for a user
  static async register(userId: string, email: string): Promise<boolean> {
    try {
      // Request registration options from server
      const response = await fetch('/api/auth/biometric/register/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Registration start failed:', response.status, errorData);
        throw new Error(errorData.error || `Failed to start registration (${response.status})`);
      }

      const options = await response.json();
      console.log('Registration options received:', options);

      if (!options.ok || !options.publicKey) {
        throw new Error('Invalid registration options from server');
      }

      // Convert base64url strings to ArrayBuffers
      const publicKeyOptions: PublicKeyCredentialCreationOptions = {
        ...options.publicKey,
        challenge: base64UrlToArrayBuffer(options.publicKey.challenge),
        user: {
          ...options.publicKey.user,
          id: base64UrlToArrayBuffer(options.publicKey.user.id),
        },
        // Force platform authenticator only - no cross-device options
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          requireResidentKey: false,
          residentKey: 'discouraged',
          userVerification: 'required',
        },
      };

      console.log('Creating credential with options:', publicKeyOptions);

      // Create credential with biometric - this should trigger Face ID/Touch ID
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      }) as BiometricCredential | null;

      if (!credential) {
        throw new Error('Credential creation cancelled or failed. Please make sure you selected "This Device" and not the QR code option.');
      }

      console.log('Credential created:', credential.id);

      // Send credential to server for verification
      const verifyResponse = await fetch('/api/auth/biometric/register/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          credential: {
            id: credential.id,
            rawId: arrayBufferToBase64Url(credential.rawId),
            type: credential.type,
            publicKey: credential.response.attestationObject 
              ? arrayBufferToBase64Url(credential.response.attestationObject)
              : undefined,
            counter: 0,
          },
        }),
      });

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json().catch(() => ({}));
        console.error('Registration complete failed:', verifyResponse.status, errorData);
        const errorMsg = errorData.message || errorData.error || `Failed to complete registration (${verifyResponse.status})`;
        throw new Error(errorMsg);
      }

      const result = await verifyResponse.json();
      console.log('Registration completed successfully:', result);

      return true;
    } catch (error) {
      console.error('Biometric registration failed:', error);
      throw error; // Re-throw to get the actual error message
    }
  }

  // Authenticate using biometric
  static async authenticate(email?: string): Promise<{ success: boolean; sessionToken?: string }> {
    try {
      // Create WebAuthn credential request
      const publicKeyOptions: PublicKeyCredentialRequestOptions = {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        timeout: 60000,
        userVerification: 'required',
        rpId: window.location.hostname,
      };

      // Get credential with biometric
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      }) as BiometricCredential | null;

      if (!credential) {
        throw new Error('Authentication cancelled');
      }

      // Send credential to server for verification
      const verifyResponse = await fetch('/api/auth/biometric/authenticate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          credential: {
            id: credential.id,
            rawId: arrayBufferToBase64Url(credential.rawId),
            type: credential.type,
            response: {
              clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
              authenticatorData: credential.response.authenticatorData
                ? arrayBufferToBase64Url(credential.response.authenticatorData)
                : undefined,
              signature: credential.response.signature
                ? arrayBufferToBase64Url(credential.response.signature)
                : undefined,
            },
          },
        }),
      });

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Authentication failed');
      }

      const result = await verifyResponse.json();
      return { success: true, sessionToken: result.sessionToken };
    } catch (error) {
      console.error('Biometric authentication failed:', error);
      return { success: false };
    }
  }

  // Check if user has biometric enabled
  static async isEnabled(): Promise<boolean> {
    try {
      const response = await fetch('/api/auth/biometric/status', {
        credentials: 'include',
      });
      
      if (!response.ok) return false;
      
      const data = await response.json();
      return data.enabled === true;
    } catch {
      return false;
    }
  }

  // Disable biometric authentication
  static async disable(): Promise<boolean> {
    try {
      const response = await fetch('/api/auth/biometric/disable', {
        method: 'POST',
        credentials: 'include',
      });
      
      return response.ok;
    } catch {
      return false;
    }
  }
}
